import { CalendarEvent, CalendarEventType } from 'shovel-lib/types';
import { BumpersPlan, PIECE_FLOOR_MINUTES, planBumpers, planBumpersEarlier } from '@utils/bump';
import { getBumpersCalendarItems, getBumpersContext } from '@state/calendar/selectors';
import { withPlanningDefaults } from '@utils/taskPlanning';
import { bumpersMode, usesBumpers } from '@utils/settingsUtils';
import { planLabel } from '@utils/timer';
import { RootState } from '@state/types';
import { toMomentDate } from 'shovel-lib/utils/timeUtils';
import { CALENDAR_DAY_START_HOUR } from './calendarConstants';
import t from '@i18n/t';

/**
 * Bumpers on the calendar (timer spec "Bumpers: dragging bumps the chain"): what dropping or stretching a session there
 * would do (bump.ts planBumpers), and the words the drag's tag and the Undo toast say about it.
 */

export type BumpersDrop = {
  /** The session dragged, when it is one already (a task dropped from the sidebar has none yet). */
  id?: string;
  start: number;
  end: number;
  /** Stretched by its bottom edge from this many minutes. */
  stretchedFrom?: number;
  /** Shift held: the other of Bump and Jump, for this drag. */
  shift?: boolean;
  /** A task from the sidebar: the free study time from the drop on (freeRoomAt). */
  room?: number;
};

/** The minutes from `at` to the end of the free study block it's in, as the calendar draws them; 0 outside one. */
export const freeRoomAt = (events: CalendarEvent[], at: number) => {
  const time = (value: any) => toMomentDate(value).valueOf();
  const block = events.find(e => e.type === CalendarEventType.STUDY_TIME && time(e.start) <= at && at < time(e.end));
  return block ? Math.round((time(block.end) - at) / 60000) : 0;
};

/**
 * What the drop would do with Bumpers on. Undefined when Bumpers is off, the session ends on an earlier day than today
 * (dragged there to log it, as before), or the calendar's awake times haven't loaded.
 */
export const planBumpersDrop = (state: RootState, theme: any, drop: BumpersDrop): BumpersPlan | undefined => {
  const settings: any = state.settings.userSettings;
  const now = Date.now();
  if (!usesBumpers(settings)) return undefined;
  const context = getBumpersContext(state, { theme });
  if (!context.awake.length) return undefined;
  // Earlier today it bumps as it does later, so the missed sessions it's dragged over move as it reaches them, not only
  // once it crosses now (Petr, 2026-10-28). An earlier day keeps the plain drag.
  if (drop.end <= now && context.dayStart !== undefined && drop.start < context.dayStart) return undefined;
  // A task from the sidebar dropped in free time stays in it, and nothing moves; what doesn't fit stays on the task (Petr,
  // 2026-11-02: a 2h task in a 50m gap "reorganizes my existing blocks"). Here it's only as long as it's dragged, up to an
  // hour, or shorter to fit (Petr, 2026-11-02: dragging a whole 3h block felt weird); the server's session then grows to
  // what the room holds. With too little room for a session it bumps, as when dropped on a session.
  if (drop.room !== undefined && drop.room >= PIECE_FLOOR_MINUTES) {
    const minutes = Math.min(Math.round((drop.end - drop.start) / 60000), drop.room);
    return { kind: 'fits', start: drop.start, end: drop.start + minutes * 60000, minutes, moves: [], creates: [], held: [] };
  }
  const input = {
    now,
    start: drop.start,
    minutes: Math.round((drop.end - drop.start) / 60000),
    stretchedFrom: drop.stretchedFrom,
    items: getBumpersCalendarItems(state, { theme }),
    breaks: withPlanningDefaults(settings?.planningPreferences),
    context,
    id: drop.id,
    carry: (bumpersMode(settings) === 'bump') !== !!drop.shift
  };
  // Dragged up from where it was (not stretched, nor new from the sidebar): it bumps the sessions it reaches earlier. With
  // Jump only within its day: from a later day it pushes later, as from an earlier one, so where it came from doesn't
  // change the result (Petr, 2026-11-02: Saturday's session dropped on Friday sent Friday's 9am to Thursday night). Bump
  // carries what it passes ahead of it, across days.
  const was = drop.id ? state.calendar.plannedEvents[drop.id as any] : undefined;
  const wasAt = was ? new Date(was.start).getTime() : undefined;
  const earlier =
    drop.stretchedFrom === undefined &&
    wasAt !== undefined &&
    drop.start < wasAt &&
    (input.carry || sameDay(drop.start, wasAt));
  return earlier ? planBumpersEarlier(input) : planBumpers(input);
};

/** Whether the drop changes nothing but the dragged session's place: then it saves as a plain move. */
export const movesOnlyItself = (plan: BumpersPlan, drop: BumpersDrop) =>
  plan.kind === 'fits' && plan.start === drop.start && plan.end === drop.end;

/**
 * Whether Shift could change the drop, Jump and Bump differing only in the sessions it passes on its way (bump.ts
 * `passedOver`): any that can move between where it was and where it goes. Cheaper than planning it both ways.
 */
export const passesSessions = (state: RootState, theme: any, drop: BumpersDrop, plan: BumpersPlan) => {
  const was = drop.id ? state.calendar.plannedEvents[drop.id as any] : undefined;
  if (!was) return false;
  const from = new Date(was.start).getTime();
  const to = new Date(was.end).getTime();
  const down = plan.start > from;
  return getBumpersCalendarItems(state, { theme }).some(
    item =>
      item.kind === 'session' &&
      item.id !== drop.id &&
      item.movable !== false &&
      (down ? item.start >= from && item.end <= plan.start : item.start >= plan.end && item.end <= to)
  );
};

/**
 * "Hold ⇧ to bump instead", in the drag's tag: on any drag that moves another session and that Shift would change, every
 * time (Petr, 2026-11-01: "It will always be a reminder ... they are a shift away from doing what they want").
 */
export const shiftHint = (state: RootState, theme: any, drop: BumpersDrop, plan?: BumpersPlan) => {
  if (!plan || !plan.moves.length || drop.shift || drop.stretchedFrom !== undefined) return undefined;
  if (!passesSessions(state, theme, drop, plan)) return undefined;
  // Planned the other way only when it could differ: the plan runs on every step of the drag.
  const other = planBumpersDrop(state, theme, { ...drop, shift: true });
  const places = (it: BumpersPlan) => `${it.start} ${it.moves.map(move => `${move.id}@${move.start}`).join(' ')}`;
  if (!other || places(other) === places(plan)) return undefined;
  return t.BUMPERS_SHIFT_TAG(bumpersMode(state.settings.userSettings as any));
};

const DAY_START_MS = CALENDAR_DAY_START_HOUR * 60 * 60 * 1000;
const sameDay = (a: number, b: number) =>
  toMomentDate(new Date(a - DAY_START_MS)).format('YYYY-MM-DD') ===
  toMomentDate(new Date(b - DAY_START_MS)).format('YYYY-MM-DD');
const dayAndDate = (dayStart: number) => toMomentDate(new Date(dayStart)).format('ddd D');
const capitalized = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** A time as the tag, toast and due pill say it: "19:10" on `reference`'s day, "Wed 08:00" another. */
const whenLabel = (at: number, reference: number, time24: boolean) => {
  const moment = toMomentDate(new Date(at));
  const time = moment.format(time24 ? 'HH:mm' : 'h:mma');
  return sameDay(at, reference) ? time : `${moment.format('ddd')} ${time}`;
};

/**
 * The red pill on a session held by its due date while a drag flows round it (Petr, 2026-10-29): "Due 11:59pm" on the
 * session's own day, "Due Fri 9:00am" another.
 */
export const dueChipText = (dueDate: number, sessionStart: number, time24: boolean) =>
  t.BUMPERS_DUE_CHIP(whenLabel(dueDate, sessionStart, time24));

/** What the tag and toast go by besides the plan. */
export type BumpersWords = {
  /** When the calendar's dates on screen start and end. */
  visibleStart?: number;
  visibleEnd?: number;
  /** Where the pointer let the session go. */
  droppedAt: number;
  time24: boolean;
};

/** Where a drop Shovel moved on lands: "19:10" the same day, "Wed 08:00" another. */
const landing = (plan: BumpersPlan, words: BumpersWords) => whenLabel(plan.start, words.droppedAt, words.time24);

/** The dropped session moved on to a day off screen: "Goes to Mon 08:00". */
const goesOffScreen = (plan: BumpersPlan, words: BumpersWords) => {
  const past = words.visibleEnd !== undefined && plan.start >= words.visibleEnd;
  const before = words.visibleStart !== undefined && plan.end <= words.visibleStart;
  return past || before ? t.BUMPERS_GOES_TO(landing(plan, words)) : undefined;
};

/** Sessions pushed off the days on screen: "some go past Sun 1", "some go before Mon 26". */
const offScreen = (plan: BumpersPlan, words: BumpersWords) => {
  if (plan.lastDay !== undefined && words.visibleEnd !== undefined && plan.lastDay >= words.visibleEnd) {
    // The last day on screen: its grid runs on to the next morning's day start (by the clock, as the night the clocks
    // go back is an hour longer).
    const last = toMomentDate(new Date(words.visibleEnd - 1));
    if (last.hours() < CALENDAR_DAY_START_HOUR) last.subtract(1, 'day');
    return t.BUMPERS_SOME_PAST(dayAndDate(last.valueOf()));
  }
  if (plan.firstDay !== undefined && words.visibleStart !== undefined && plan.firstDay < words.visibleStart)
    return t.BUMPERS_SOME_BEFORE(dayAndDate(words.visibleStart));
  return undefined;
};

/**
 * The tag by the dragged block, only for what can't be seen as the blocks slide and the landing outline shows (Petr,
 * 2026-10-28: the rest is a distraction): something going past the days on screen, or no room. "Goes to Mon 08:00",
 * "Some go past Sat 31", "Goes to Mon 08:00 · some go past Sat 31", "Shortened to 30m: no room left this term". None
 * on every other drag.
 */
export const bumpersTag = (plan: BumpersPlan, words: BumpersWords) => {
  if (plan.kind === 'refused') return t.BUMPERS_REFUSED;
  if (plan.kind === 'shortened') return t.BUMPERS_SHORTENED(planLabel(plan.minutes));
  const parts = [goesOffScreen(plan, words), offScreen(plan, words)].filter((part): part is string => !!part);
  return parts.length ? capitalized(parts.join(' · ')) : undefined;
};

/**
 * The Undo toast once it's saved, kept short (Petr, 2026-10-28: the blocks show where they went, and Undo is there):
 * "Moved “Chemistry” and 4 more", "Moved “Chemistry” to Wed 08:00 and 3 more"; a task dropped from the sidebar,
 * "Planned “Chemistry”, moved 2 more"; a stretched one, "Stretched “Chemistry” to 1h 30m, moved 1 more".
 */
export const bumpersToast = (
  plan: BumpersPlan,
  title: string | undefined,
  words: BumpersWords,
  { planned = false, stretched = false } = {}
) => {
  const name = title ? `“${title}”` : t.BUMPERS_THE_SESSION;
  const others = plan.moves.length;
  if (stretched) return t.BUMPERS_STRETCHED(name, planLabel(plan.minutes), others);
  const what = plan.kind === 'shortened' ? t.BUMPERS_SHORTENED_TO(name, planLabel(plan.minutes)) : name;
  const at = plan.start !== words.droppedAt ? landing(plan, words) : undefined;
  return planned ? t.BUMPERS_PLANNED(what, at, others) : t.BUMPERS_MOVED(what, at, others);
};
